import { useTranslation } from "@/hooks/localization/useTranslations";
import SvgAddToCard from "./btn-expanding-icons/SvgAddToCard";
import SvgMinus from "./btn-expanding-icons/SvgMinus";
import SvgPlus from "./btn-expanding-icons/SvgPlus";
import SvgRecycleBin from "./btn-expanding-icons/SvgRecycleBin";
import SvgQuestionMark from "@/app/components/lib/SvgQuestionMark";
import Tooltip from "@/app/components/lib/tooltip";
import Badge from "@/app/components/lib/badge";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useState } from "react";
import AppToast from "@/app/components/lib/toast/AppToast";
import ExpandingQuantityInput from "./ExpandingQuantityInput";
import { IEventCustomData } from "@/api/AppApi/services/home/IEvent";
import ReminderButton from "../ReminderButton";
import { IProduct } from "@/api/AppApi/services/home/interfaces/IProduct";

interface Props {
  quantity: number;
  increaseQty: (e: React.MouseEvent, value?: number) => void;
  decreaseQty: (value?: number) => void;
  viewDetails: boolean;
  outOfStock: boolean;
  maxQuantity: number;
  minQuantity: number;
  enable_sells: boolean;
  viewDetailsClick: () => void;
  HBFullWidth: boolean;
  isConfigurableProduct: boolean;
  addToCartTxt?: string;
  handleChangeQty: (value: number) => void;
  preview_type: string;
  custom_data?: IEventCustomData;
  addedToCart: boolean;
  isLoading: boolean;
  choose_variant?: boolean;
  shouldReminder?: boolean;
  productProps?: IProduct;
}

const HBtnExpandingAddToCard: React.FC<Props> = ({
  quantity,
  increaseQty,
  decreaseQty,
  viewDetails,
  outOfStock,
  maxQuantity,
  minQuantity,
  enable_sells,
  viewDetailsClick,
  HBFullWidth,
  isConfigurableProduct,
  addToCartTxt,
  handleChangeQty,
  preview_type,
  custom_data,
  addedToCart,
  isLoading,
  choose_variant,
  shouldReminder,
  productProps,
}) => {
  const t = useTranslation();
  const [openToast, setOpenToast] = useState(false);
  const isMobile = useIsMobile();
  const { enable_registration } = custom_data ?? {};

  const disabled =
    outOfStock ||
    !(enable_sells || viewDetails) ||
    (preview_type === "event" && !enable_registration);
  const valueToDecrease = quantity === minQuantity ? minQuantity : 1;

  const btnChildren = () => {
    return (
      <>
        {viewDetails && !isConfigurableProduct ? (
          t.view
        ) : choose_variant ? (
          t.choose_variant
        ) : outOfStock ? (
          t.out_of_stock
        ) : enable_sells ? (
          <>
            <span style={{ marginInlineEnd: 4 }}>
              <SvgAddToCard small />
            </span>
            {addToCartTxt ? addToCartTxt : t?.add_to_cart}
          </>
        ) : (
          t.sell_disabled
        )}
      </>
    );
  };

  if (shouldReminder && outOfStock) {
    return (
      <ReminderButton
        small={false}
        product={{
          name: productProps?.name!,
          description: productProps?.description!,
          image: productProps?.image_url!,
          rate: productProps?.rate,
          id: productProps?.id!,
        }}
      />
    );
  }

  return (
    <>
      {!addedToCart ? (
        <button
          className={`btn-expanded-h-add-to-card ${HBFullWidth ? "w-100" : ""}`}
          onClick={(e) =>
            viewDetails ? viewDetailsClick() : increaseQty(e, minQuantity)
          }
          disabled={disabled || isLoading || choose_variant}
        >
          {isLoading ? (
            <span
              className="btn-loader"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg width="20" height="20" viewBox="0 0 50 50">
                <circle
                  cx="25"
                  cy="25"
                  r="20"
                  fill="none"
                  stroke="#999"
                  strokeWidth="5"
                  strokeDasharray="31.4 31.4"
                  strokeLinecap="round"
                >
                  <animateTransform
                    attributeName="transform"
                    type="rotate"
                    from="0 25 25"
                    to="360 25 25"
                    dur="1s"
                    repeatCount="indefinite"
                  />
                </circle>
              </svg>
            </span>
          ) : (
            btnChildren()
          )}
        </button>
      ) : isConfigurableProduct || viewDetails || preview_type === "event" ? (
        <button
          className={`btn-expanded-h-added-to-card ${
            HBFullWidth ? "w-100" : ""
          }`}
          onClick={viewDetailsClick}
        >
          <>
            <span style={{ marginInlineEnd: 4 }}>
              <SvgAddToCard small />
            </span>
            {t.btn_added_to_cart}
          </>
        </button>
      ) : (
        <div className={`btn-expanded-h-add-to-card-counter`}>
          <div onClick={(e) => quantity < maxQuantity && increaseQty(e)}>
            <SvgPlus disabled={quantity === maxQuantity} />
          </div>
          {minQuantity > 1 && minQuantity === quantity ? (
            <div
              style={{ display: "flex", flexDirection: "column" }}
              onClick={() => {
                isMobile && setOpenToast(true);
              }}
            >
              <Tooltip
                text={`${t.min_quantity_in_cart} ${minQuantity}`}
                skip={isMobile}
                paddingTop={20}
              >
                <Badge
                  badgeComponent={<SvgQuestionMark />}
                  position="top-right"
                >
                  <ExpandingQuantityInput
                    quantity={quantity}
                    setQuantity={handleChangeQty}
                  />
                </Badge>
              </Tooltip>
            </div>
          ) : (
            <ExpandingQuantityInput
              quantity={quantity}
              setQuantity={handleChangeQty}
            />
          )}
          {quantity > minQuantity && (
            <div onClick={() => decreaseQty(valueToDecrease)}>
              <SvgMinus />
            </div>
          )}
          <div onClick={() => handleChangeQty(0)}>
            <SvgRecycleBin />
          </div>
        </div>
      )}

      <AppToast
        message={`${t.min_quantity_in_cart} ${minQuantity}`}
        setShow={setOpenToast}
        show={openToast}
        paddingBottom={80}
        width="300px"
        cancelIcon={false}
        delay={3000}
      />
    </>
  );
};

export default HBtnExpandingAddToCard;
